import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DirectoryPicker, dirEmail, dirLabel } from '../components/DirectoryPicker'
import type { DirectoryUser } from '../components/DirectoryPicker'
import { GroupPicker, groupLabel } from '../components/GroupPicker'
import type { DirectoryGroup } from '../components/GroupPicker'
import { StatCard } from '../components/StatCard'
import { PersonView } from './PersonView'
import { AgentMatrix } from './AgentMatrix'

type PrincipalType = 'group' | 'user'

/** Matches `GET /api/access` in `backend/src/routes/access.ts`. */
interface Grant {
  principalType: PrincipalType
  principalId: string
  principalName?: string
  grantedBy: string
  grantedAt: string
  reason: string | null
}

interface ResourceAccess {
  id: string
  displayName: string
  sensitive: boolean
  /** True once the server actually reads its SSM param at runtime. */
  enforced: boolean
  grants: Grant[]
}

/** Matches `GET /api/status/access-report` in `backend/src/routes/status.ts`. */
interface GroupGrant {
  id: string
  name: string
  memberCount: number
  error?: string
}
interface ServerGrant {
  server: string
  displayName: string
  sensitive: boolean
  groups: GroupGrant[]
  directUsers: string[]
  effectiveEmails: string[]
}
interface RoleGroupMembership {
  group: string
  members: string[]
  error?: string
}
interface AccessReport {
  serverGrants: ServerGrant[]
  roleGroups: RoleGroupMembership[]
  note: string
}

interface PendingChange {
  resourceId: string
  resourceDisplayName: string
  principalType: PrincipalType
  principalId: string
  principalLabel: string
  kind: 'grant' | 'revoke'
}

/** One row of the matrix: a principal, and the servers it reaches. */
interface MatrixRow {
  principalType: PrincipalType
  principalId: string
  label: string
  /** resourceId -> the grant that put it there. */
  granted: Map<string, Grant>
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (HTTP ${err.status})`
  }
  return err instanceof Error ? err.message : String(err)
}

/** Short column header for a server id: `chu-telephony` -> `tel`. */
function shortId(resourceId: string): string {
  return resourceId.replace(/^chu-/, '').slice(0, 4)
}

/**
 * Access: one page, two clearly separated planes.
 *
 * This replaces the old pair of pages (a read-only "Access" report and a
 * separate "Access grants" editor listing the same servers again). The
 * editor moves INTO the matrix, which is what makes the merge worth doing:
 * one grid where a cell is both the fact and the control.
 *
 * THE RULING THAT SHAPES THIS PAGE: two mechanisms never share one grid.
 *
 * MCP access is resolved live at the server on every request, from SSM plus
 * a live Entra group check. LibreChat agent shares are resolved from a Mongo
 * membership cache written at sign-in and never pruned. They disagree today
 * and they will disagree again — CHU-GPT-Management has three members in
 * Entra and one in LibreChat's roster. Someone added to a group has
 * warehouse access in about six minutes and cannot see that group's agent
 * until they next sign in; someone removed loses the warehouse in six
 * minutes and keeps seeing the agent indefinitely.
 *
 * Drawing both as ticks in one matrix would assert an agreement that does
 * not exist, and an operator who trusted it would offboard someone and
 * believe it was done. So the LIVE plane is a real grid, the CACHED plane is
 * a stated absence, and each says which mechanism it is.
 */
export function AccessPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  const [resources, setResources] = useState<ResourceAccess[] | null>(null)
  const [report, setReport] = useState<AccessReport | null>(null)
  const [reportError, setReportError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyResourceId, setBusyResourceId] = useState<string | null>(null)

  // View A's person picker. Kept in the URL (?person=<email>) so the People
  // page can link straight here, and so an operator can paste a colleague's
  // page to someone else. `history.replaceState` rather than a router push:
  // picking a person is not a navigation, and it must not stack back-button
  // entries for every name typed.
  const [person, setPerson] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('person'),
  )
  const selectPerson = useCallback((email: string | null) => {
    setPerson(email)
    const url = new URL(window.location.href)
    if (email) {
      url.searchParams.set('person', email)
    } else {
      url.searchParams.delete('person')
    }
    window.history.replaceState(null, '', url.toString())
  }, [])

  const [addOpen, setAddOpen] = useState<PrincipalType | null>(null)
  const [pending, setPending] = useState<PendingChange | null>(null)
  const [pendingReason, setPendingReason] = useState('')
  const [pendingSubmitting, setPendingSubmitting] = useState(false)
  const [pendingError, setPendingError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiJson<{ resources: ResourceAccess[] }>('/api/access')
      setResources(data.resources)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  // The live-resolution report is a SEPARATE read and a separate failure:
  // it is what each server computes right now (SSM + Graph), where
  // /api/access is the stored grant set. If this one cannot be read the
  // matrix is still true, but the member counts are unknown rather than
  // zero, and the page has to say which.
  const loadReport = useCallback(async () => {
    setReportError(null)
    try {
      setReport(await apiJson<AccessReport>('/api/status/access-report'))
    } catch (err) {
      setReportError(errorMessage(err))
    }
  }, [])

  useEffect(() => {
    void load()
    void loadReport()
  }, [load, loadReport])

  async function applyChange(change: PendingChange, reason?: string): Promise<void> {
    setBusyResourceId(change.resourceId)
    setError(null)
    try {
      await apiJson(`/api/access/${change.kind === 'grant' ? 'grant' : 'revoke'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resourceId: change.resourceId,
          principalType: change.principalType,
          principalId: change.principalId,
          ...(reason ? { reason } : {}),
        }),
      })
      await load()
      await loadReport()
    } catch (err) {
      throw err instanceof Error ? err : new Error(String(err))
    } finally {
      setBusyResourceId(null)
    }
  }

  function requestChange(
    resource: ResourceAccess,
    principalType: PrincipalType,
    principalId: string,
    principalLabel: string,
    kind: 'grant' | 'revoke',
  ) {
    // A sensitive resource always takes the reason + confirm path, in BOTH
    // directions. Unchanged from the old editor: it is a PIA obligation,
    // not a UI preference, and the matrix does not get to skip it just
    // because the control is now a cell.
    if (resource.sensitive) {
      setPending({
        resourceId: resource.id,
        resourceDisplayName: resource.displayName,
        principalType,
        principalId,
        principalLabel,
        kind,
      })
      setPendingReason('')
      setPendingError(null)
      return
    }
    void applyChange({
      resourceId: resource.id,
      resourceDisplayName: resource.displayName,
      principalType,
      principalId,
      principalLabel,
      kind,
    }).catch((err: unknown) => setError(errorMessage(err)))
  }

  async function confirmPending() {
    if (!pending) {
      return
    }
    setPendingSubmitting(true)
    setPendingError(null)
    try {
      await applyChange(pending, pendingReason.trim())
      setPending(null)
    } catch (err) {
      setPendingError(errorMessage(err))
    } finally {
      setPendingSubmitting(false)
    }
  }

  /**
   * The matrix rows. Groups first, then direct user grants — which are
   * exceptions, so they are always shown and always at the bottom rather
   * than mixed in where they read as ordinary.
   */
  const rows = useMemo((): { groups: MatrixRow[]; users: MatrixRow[] } => {
    const byPrincipal = new Map<string, MatrixRow>()
    for (const resource of resources ?? []) {
      for (const grant of resource.grants) {
        const key = `${grant.principalType}:${grant.principalId}`
        let row = byPrincipal.get(key)
        if (!row) {
          row = {
            principalType: grant.principalType,
            principalId: grant.principalId,
            label: grant.principalName ?? grant.principalId,
            granted: new Map(),
          }
          byPrincipal.set(key, row)
        }
        row.granted.set(resource.id, grant)
      }
    }
    const all = [...byPrincipal.values()].sort((a, b) => a.label.localeCompare(b.label))
    return {
      groups: all.filter((r) => r.principalType === 'group'),
      users: all.filter((r) => r.principalType === 'user'),
    }
  }, [resources])

  const memberCountFor = useCallback(
    (groupId: string): number | null => {
      for (const server of report?.serverGrants ?? []) {
        const match = server.groups.find((g) => g.id === groupId)
        if (match && !match.error) {
          return match.memberCount
        }
      }
      return null
    },
    [report],
  )

  const distinctAllowed = useMemo(() => {
    const set = new Set<string>()
    for (const s of report?.serverGrants ?? []) {
      for (const email of s.effectiveEmails) {
        set.add(email)
      }
    }
    return set.size
  }, [report])

  const groupLookupErrors =
    (report?.serverGrants.reduce((sum, s) => sum + s.groups.filter((g) => g.error).length, 0) ?? 0) +
    (report?.roleGroups.filter((g) => g.error).length ?? 0)

  const resourceList = resources ?? []

  function addPrincipal(resource: ResourceAccess, type: PrincipalType, id: string, label: string) {
    setAddOpen(null)
    requestChange(resource, type, id, label, 'grant')
  }

  const cellButton = (resource: ResourceAccess, row: MatrixRow) => {
    const grant = row.granted.get(resource.id)
    const granted = grant !== undefined
    const busy = busyResourceId === resource.id
    const readOnly = !resource.enforced
    const verb = granted ? 'Revoke' : 'Grant'
    return (
      <button
        type="button"
        disabled={busy || readOnly}
        aria-label={`${verb} ${resource.displayName} for ${row.label}`}
        aria-pressed={granted}
        title={
          readOnly
            ? `${resource.displayName} is still managed in terraform; edits are disabled.`
            : granted
              ? `Granted by ${grant.grantedBy} on ${grant.grantedAt}. Click to revoke.`
              : `Click to grant ${resource.displayName} to ${row.label}.`
        }
        onClick={() =>
          requestChange(resource, row.principalType, row.principalId, row.label, granted ? 'revoke' : 'grant')
        }
        style={{
          width: '1.6rem',
          height: '1.6rem',
          borderRadius: '0.35rem',
          cursor: busy || readOnly ? 'not-allowed' : 'pointer',
          border: `1px solid ${granted ? (resource.sensitive ? tokens.danger : tokens.brand) : tokens.border}`,
          background: granted ? (resource.sensitive ? tokens.danger : tokens.brand) : 'transparent',
          color: granted ? (resource.sensitive ? tokens.dangerContrast : tokens.brandContrast) : tokens.textSecondary,
          fontSize: '0.8rem',
          lineHeight: 1,
          opacity: readOnly ? 0.4 : 1,
        }}
      >
        {granted ? '✓' : ''}
      </button>
    )
  }

  const headerCell: React.CSSProperties = {
    padding: '0.3rem 0.2rem',
    fontSize: '0.7rem',
    color: tokens.textSecondary,
    fontWeight: 600,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  }

  const matrixTable = (matrixRows: MatrixRow[], emptyLabel: string) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.84rem' }}>
        <thead>
          <tr>
            <th style={{ ...headerCell, textAlign: 'left', paddingRight: '1rem' }}>Principal</th>
            {resourceList.map((resource) => (
              <th key={resource.id} style={headerCell} title={`${resource.displayName} (${resource.id})`}>
                {shortId(resource.id)}
                {resource.sensitive ? <span style={{ color: tokens.danger }}> *</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrixRows.length === 0 ? (
            <tr>
              <td colSpan={resourceList.length + 1} style={{ ...styles.hint, padding: '0.6rem 0' }}>
                {emptyLabel}
              </td>
            </tr>
          ) : null}
          {matrixRows.map((row) => {
            const members = row.principalType === 'group' ? memberCountFor(row.principalId) : null
            return (
              <tr key={`${row.principalType}:${row.principalId}`} aria-label={row.label}>
                <td
                  style={{
                    padding: '0.3rem 1rem 0.3rem 0',
                    color: tokens.textPrimary,
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${tokens.border}`,
                  }}
                >
                  {row.principalType === 'user' ? (
                    // `principalId` IS the email for a user row (see
                    // `AddPrincipal`'s own `setPicked({ id: email, ... })`
                    // below) -- the one addition the 23 Sep review asked for
                    // here: a link from every principal cell to that
                    // person's page, never a re-implementation of it.
                    <a href={`/people?person=${encodeURIComponent(row.principalId)}`} style={{ color: tokens.textPrimary }}>
                      {row.label}
                    </a>
                  ) : (
                    row.label
                  )}
                  {row.principalType === 'group' ? (
                    <span style={{ ...styles.hint, marginLeft: '0.4rem' }}>
                      {/* Member counts resolve LIVE via Graph, which is the
                          same path the servers use. Unknown is printed as
                          "?" rather than 0: an unreadable group is not an
                          empty one. */}
                      {reportError ? '?' : members === null ? '?' : `${members}`}
                    </span>
                  ) : null}
                </td>
                {resourceList.map((resource) => (
                  <td
                    key={resource.id}
                    style={{ textAlign: 'center', padding: '0.25rem', borderBottom: `1px solid ${tokens.border}` }}
                  >
                    {cellButton(resource, row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <div style={{ ...styles.page, maxWidth: 'none' }}>
      {/*
        The legend, rendered on the page rather than only in the design doc.
        A reader has to be able to tell which plane they are looking at and
        why the two can disagree, without having read anything else.
      */}
      <div style={styles.callout} role="note">
        <strong>Two mechanisms, two planes. They are not the same boundary and they can disagree.</strong>
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          <div>
            <span
              style={{
                ...styles.badge,
                background: tokens.brand,
                color: tokens.brandContrast,
                marginRight: '0.5rem',
              }}
            >
              Live
            </span>
            <strong>MCP server access.</strong> Decided at each server on every request from{' '}
            <code>/chu-gpt/access/&lt;server&gt;</code> plus a live Entra group check. Governs the web app{' '}
            <em>and</em> Teams. Revocation bites in about six minutes.
          </div>
          <div>
            <span
              style={{
                ...styles.badge,
                background: tokens.surfaceRaised,
                color: tokens.textSecondary,
                border: `1px solid ${tokens.border}`,
                marginRight: '0.5rem',
              }}
            >
              Cached
            </span>
            <strong>Agent shares.</strong> Decided inside LibreChat from Mongo ACL rows whose group roster is written
            at sign-in and never pruned. It is <em>not</em> the MCP boundary and never has been.
          </div>
          <div style={{ color: tokens.textSecondary }}>
            So: adding someone to a group gives them the warehouse in about six minutes and gives them that group's
            agents only when they next sign in. Removing them takes the warehouse away in six minutes and leaves the
            agents visible indefinitely. One grid with one tick cannot say that, which is why there are two.
          </div>
        </div>
      </div>

      {/* ------------------------------- VIEW A ------------------------------
          The person view. It is placed FIRST because it is the view that
          answers questions: an operator arrives here holding a name, not a
          server. The two org-wide matrices below are the inventory. */}
      <section style={styles.section} aria-label="Person view">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Look up a person</h2>
        </div>
        <p style={styles.sectionSubtitle}>
          Every MCP server they can reach and every agent they can see, on one page, each with the mechanism and the
          grant that allows it — and an explicit finding when the two sources disagree. Rows on the People page link
          here too.
        </p>
        <DirectoryPicker
          tokens={tokens}
          placeholder="Type a colleague's name or email…"
          onPick={(u) => selectPerson(dirEmail(u).toLowerCase())}
        />
      </section>

      {person ? <PersonView tokens={tokens} email={person} onClear={() => selectPerson(null)} /> : null}

      <div style={styles.statsRow}>
        <StatCard label="Governed servers" value={resourceList.length} tokens={tokens} />
        <StatCard
          label="Distinct users with access"
          value={reportError ? '?' : distinctAllowed}
          hint={reportError ? 'live report unreadable' : 'union across all servers, resolved live'}
          tokens={tokens}
          tone={reportError ? 'danger' : 'default'}
        />
        <StatCard label="Granted groups" value={rows.groups.length} tokens={tokens} />
        <StatCard
          label="Direct user grants"
          value={rows.users.length}
          hint="exceptions"
          tokens={tokens}
          tone={rows.users.length > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Group lookup errors"
          value={groupLookupErrors}
          tokens={tokens}
          tone={groupLookupErrors > 0 ? 'danger' : 'default'}
        />
      </div>

      {error ? <p style={styles.error}>{error}</p> : null}
      {loading ? <p style={styles.hint}>Loading access…</p> : null}

      {/* ---------------------------- LIVE PLANE ---------------------------- */}
      <section style={styles.section} aria-label="MCP server access, live">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            MCP server access{' '}
            <span style={{ ...styles.badge, background: tokens.brand, color: tokens.brandContrast }}>Live</span>
          </h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => {
              void load()
              void loadReport()
            }}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Decided at each server on every request from <code>/chu-gpt/access/&lt;server&gt;</code> plus a live Entra
          group check. Governs BOTH the web app and Teams. Revocation takes effect in about six minutes. A cell is the
          fact and the control: click to grant, click again to revoke. Sensitive servers (marked{' '}
          <span style={{ color: tokens.danger }}>*</span>) require a typed reason and a confirm, in both directions.
          Sources: SSM <code>/chu-gpt/access/*</code> for the grants, Graph for the member counts, both read now.
        </p>
        {reportError ? (
          <p style={styles.error}>
            The live member counts could not be read, so every count below is unknown rather than zero:{' '}
            {reportError}. The grant matrix itself is unaffected — it comes from a different read.
          </p>
        ) : null}

        <h3 style={{ ...styles.fieldLabel, marginTop: '0.4rem' }}>Groups</h3>
        {matrixTable(rows.groups, 'No groups granted to any server.')}

        <h3 style={{ ...styles.fieldLabel, marginTop: '1.4rem' }}>
          Direct user grants <span style={styles.hint}>— exceptions, always shown, always at the bottom</span>
        </h3>
        {matrixTable(rows.users, 'No individual users granted. Good: access should come from a group.')}

        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.2rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => setAddOpen(addOpen === 'group' ? null : 'group')}
          >
            + Add a group to the matrix
          </button>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => setAddOpen(addOpen === 'user' ? null : 'user')}
          >
            + Add a direct user grant
          </button>
        </div>
        {addOpen ? (
          <div style={{ marginTop: '0.8rem' }}>
            <p style={styles.hint}>
              Pick a {addOpen === 'group' ? 'group' : 'person'}, then choose which server to grant. A new row appears
              once the first grant is made.
            </p>
            <AddPrincipal
              tokens={tokens}
              type={addOpen}
              resources={resourceList}
              onGrant={addPrincipal}
              disabled={busyResourceId !== null}
            />
          </div>
        ) : null}
      </section>

      {/* --------------------------- CACHED PLANE --------------------------- */}
      <section style={styles.section} aria-label="Agent shares, cached">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            Agent shares{' '}
            <span
              style={{
                ...styles.badge,
                background: tokens.surfaceRaised,
                color: tokens.textSecondary,
                border: `1px solid ${tokens.border}`,
              }}
            >
              Cached
            </span>
          </h2>
        </div>
        <p style={styles.sectionSubtitle}>
          Decided inside LibreChat from Mongo ACL rows against group documents. Those documents carry a{' '}
          <code>memberIds</code> roster written at sign-in and never pruned, so this plane can be wrong in both
          directions. It is not the MCP boundary.
        </p>
        {/* Item 10 landed: this is a real grid now, read over a read-only
            Mongo window (27017 egress to the mongodb security group, and the
            MONGO_URI json key alone — the panel never holds the rest of
            chu-gpt/librechat/app). It stays a SEPARATE grid from the LIVE
            matrix above, and its cells carry n/m rather than ticks, because
            the two mechanisms are still not the same boundary. */}
        <AgentMatrix tokens={tokens} />
      </section>

      {/* Role-group membership, folded in from the old read-only Access page
          so the merge loses nothing. */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Role-group membership</h2>
        <p style={styles.sectionSubtitle}>
          CHU-GPT-* capability groups, direct members only, no nesting. Resolved live via Graph — the same path each
          server uses to decide a request.
        </p>
        {reportError ? (
          <p style={styles.error}>Unreadable, so unknown rather than empty: {reportError}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {(report?.roleGroups ?? []).map((group) => (
              <div
                key={group.group}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.6rem',
                  flexWrap: 'wrap',
                  border: `1px solid ${tokens.border}`,
                  borderLeft: `3px solid ${group.error ? tokens.danger : tokens.brand}`,
                  borderRadius: '0.6rem',
                  background: tokens.surfaceRaised,
                  padding: '0.55rem 0.9rem',
                }}
              >
                <span style={{ color: tokens.textPrimary, fontWeight: 600 }}>{group.group}</span>
                <span style={styles.hint}>
                  {group.error ? group.error : `${group.members.length} members · ${group.members.join(', ') || '-'}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {pending ? (
        <div style={styles.modalOverlay} role="presentation" onClick={() => !pendingSubmitting && setPending(null)}>
          <div
            style={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-label={`${pending.kind === 'grant' ? 'Grant' : 'Revoke'} access — ${pending.resourceDisplayName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>
              {pending.kind === 'grant' ? 'Grant' : 'Revoke'} access — {pending.resourceDisplayName}
            </h3>
            <div style={{ ...styles.warningCallout, marginBottom: '1rem' }}>
              This triggers a PIA (privacy impact assessment) obligation: {pending.resourceDisplayName} carries data
              about identifiable people. {pending.kind === 'grant' ? 'Granting' : 'Revoking'} access to{' '}
              {pending.principalType === 'group' ? 'the group' : 'the user'} <strong>{pending.principalLabel}</strong>{' '}
              is recorded in the audit log with the reason below.
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void confirmPending()
              }}
            >
              <div style={styles.field}>
                <label style={styles.fieldLabel} htmlFor="pending-reason">
                  Reason (required)
                </label>
                <textarea
                  id="pending-reason"
                  style={styles.textarea}
                  rows={3}
                  value={pendingReason}
                  onChange={(e) => setPendingReason(e.target.value)}
                  disabled={pendingSubmitting}
                  placeholder="Why is this change needed?"
                />
              </div>
              {pendingError ? <p style={styles.error}>{pendingError}</p> : null}
              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.buttonGhost}
                  onClick={() => setPending(null)}
                  disabled={pendingSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={pending.kind === 'revoke' ? styles.buttonDanger : styles.button}
                  disabled={pendingSubmitting || pendingReason.trim().length === 0}
                >
                  {pendingSubmitting ? 'Saving…' : pending.kind === 'grant' ? 'Confirm grant' : 'Confirm revoke'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Adds a principal that has no row yet: pick the principal, pick the first
 * server, and the grant creates the row. Split out so the matrix itself
 * stays a matrix.
 */
function AddPrincipal({
  tokens,
  type,
  resources,
  onGrant,
  disabled,
}: {
  tokens: ThemeTokens
  type: PrincipalType
  resources: ResourceAccess[]
  onGrant: (resource: ResourceAccess, type: PrincipalType, id: string, label: string) => void
  disabled: boolean
}) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(null)

  if (!picked) {
    return type === 'group' ? (
      <GroupPicker
        tokens={tokens}
        disabled={disabled}
        excludeIds={[]}
        onPick={(g: DirectoryGroup) => setPicked({ id: g.id, label: groupLabel(g) })}
      />
    ) : (
      <DirectoryPicker
        tokens={tokens}
        disabled={disabled}
        excludeIds={[]}
        onPick={(u: DirectoryUser) => {
          const email = dirEmail(u)
          if (email) {
            setPicked({ id: email, label: dirLabel(u) })
          }
        }}
      />
    )
  }

  return (
    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={styles.chip}>{picked.label}</span>
      <span style={styles.hint}>grant to:</span>
      {resources
        .filter((r) => r.enforced)
        .map((resource) => (
          <button
            key={resource.id}
            type="button"
            style={styles.buttonGhost}
            disabled={disabled}
            onClick={() => {
              onGrant(resource, type, picked.id, picked.label)
              setPicked(null)
            }}
          >
            {resource.displayName}
            {resource.sensitive ? <span style={{ color: tokens.danger }}> *</span> : null}
          </button>
        ))}
      <button type="button" style={styles.buttonGhost} onClick={() => setPicked(null)}>
        Cancel
      </button>
    </div>
  )
}

export default AccessPage
